import { forwardRef, ReactNode, useRef, useState } from 'react';
import 'swiper/css';
import 'swiper/css/pagination';
import { Mousewheel, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

interface SwipeContainerProps {
  children: ReactNode;
  onIndexChange?: (index: number) => void;
  showPagination?: boolean;
  paginationStyle?: 'default' | 'custom';
  disableHeightFlex?: boolean;
}

const SwipeContainer = forwardRef<HTMLDivElement, SwipeContainerProps>(
  (
    {
      children,
      onIndexChange,
      showPagination = false,
      paginationStyle = 'default',
      disableHeightFlex = false,
    },
    ref,
  ) => {
    const swiperRef = useRef<any>(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const childrenArray = Array.isArray(children) ? children : [children];

    const handleSlideChange = (swiper: any) => {
      setCurrentIndex(swiper.activeIndex);
      if (onIndexChange) {
        onIndexChange(swiper.activeIndex);
      }
    };

    const goToSlide = (index: number) => {
      if (swiperRef.current?.swiper) {
        swiperRef.current.swiper.slideTo(index);
      }
    };

    return (
      <div ref={ref} className="relative">
        <Swiper
          ref={swiperRef}
          modules={showPagination ? [Mousewheel, Pagination] : [Mousewheel]}
          spaceBetween={0}
          slidesPerView={1}
          mousewheel={{
            forceToAxis: true,
            sensitivity: 1,
            thresholdDelta: 50,
          }}
          grabCursor={true}
          allowTouchMove={true}
          touchRatio={1}
          touchAngle={45}
          resistanceRatio={0}
          longSwipes={true}
          longSwipesRatio={0.5}
          longSwipesMs={300}
          followFinger={true}
          shortSwipes={true}
          threshold={5}
          onSlideChange={handleSlideChange}
          pagination={
            showPagination && paginationStyle === 'default'
              ? {
                  clickable: true,
                  bulletClass:
                    'swiper-pagination-bullet !bg-[#8670BB] !opacity-100',
                  bulletActiveClass:
                    'swiper-pagination-bullet-active !bg-[#4A16B8]',
                }
              : false
          }
          className={`select-none ${!disableHeightFlex ? '[&_.swiper-wrapper]:flex [&_.swiper-wrapper]:items-end' : ''}`}
        >
          {childrenArray.map((child, index) => (
            <SwiperSlide key={index}>{child}</SwiperSlide>
          ))}
        </Swiper>

        {/* Custom pagination dots */}
        {showPagination &&
          paginationStyle === 'custom' &&
          childrenArray.length > 1 && (
            <div className="flex justify-center gap-2 pointer-events-none my-2">
              {childrenArray.map((_, index) => (
                <button
                  key={index}
                  className={`h-2 rounded-full transition-all duration-200 pointer-events-auto ${
                    currentIndex === index
                      ? 'bg-[#4A16B8] w-6'
                      : 'bg-[#8670BB] hover:bg-[#4A16B8] w-2'
                  }`}
                  onClick={() => goToSlide(index)}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          )}
      </div>
    );
  },
);

SwipeContainer.displayName = 'SwipeContainer';

export default SwipeContainer;
